/* ERP color gallery v1, for the LVS media-gallery / product-info integration. */
(() => {
  'use strict';
  if (window.ErpColorGallery) return;
  const numeric = value => typeof value === 'string' && /^\d+$/.test(value);
  const identity = value => String(value || '').match(/(?:^|-)(\d+)$/)?.[1] || '';
  function resolve(config, colorId, mediaIds) {
    const all = [...new Set(mediaIds)], available = new Set(all);
    const fallback = {ids: all, first: all[0] || '', filtered: false};
    if (!config || config.source !== 'erp-color-gallery' || config.schemaVersion !== 1 || config.enabled !== true || !Array.isArray(config.groups)) return fallback;
    const matches = config.groups.filter(group => group.optionValueId === colorId);
    if (matches.length !== 1 || !Array.isArray(matches[0].mediaIds)) return fallback;
    const own = matches[0].mediaIds.filter(id => numeric(id) && available.has(id));
    if (!own.length) return fallback; // Deleted/replaced photos must never yield an empty gallery.
    const shared = Array.isArray(config.commonMediaIds) ? config.commonMediaIds.filter(id => numeric(id) && available.has(id)) : [];
    const allowed = new Set([...own, ...shared]);
    return {ids: all.filter(id => allowed.has(id)), first: own[0], filtered: true};
  }
  class Controller {
    constructor(gallery, data, document) {
      this.gallery = gallery; this.data = data; this.document = document;
      this.root = gallery.closest('product-info'); this.section = gallery.dataset.section;
      this.color = undefined; this.variant = data.initialVariantId;
      this.originalSetActiveMedia = gallery.setActiveMedia.bind(gallery);
      gallery.prependMedia = false;
      // Grid layouts still become sliders on narrow screens; native grid mode does not track swipes.
      gallery.querySelector('[id^="GalleryViewer"]')?.addEventListener('slideChanged', event => {
        const current = event.detail?.currentElement;
        if (!current?.dataset.mediaId || current.hasAttribute('data-erp-color-hidden')) return;
        gallery.querySelectorAll('.product__media-item[data-media-id]').forEach(slide => slide.classList.toggle('is-active', slide === current));
        this.refreshDots();
      });
      gallery.setActiveMedia = (id, prepend, filtering) => {
        const result = this.filter();
        const active = gallery.querySelector('.product__media-item.is-active');
        const requested = identity(id), current = identity(active?.dataset.mediaId);
        const automatic = prepend || filtering;
        const keepCurrent = automatic && !result.changed && result.ids.includes(current);
        const target = keepCurrent ? current
          : automatic && result.changed ? result.first
          : result.ids.includes(requested) ? requested : result.first;
        // A Paper-only update must not trigger the native gallery's delayed window scroll.
        if (target && !keepCurrent) this.originalSetActiveMedia(`${this.section}-${target}`, false, false, null);
        this.refreshSliders();
      };
    }
    selectedVariant() {
      const form = this.root?.querySelector('form input[name="id"]')?.value;
      if (form && form !== this.lastFormVariant) { this.lastFormVariant = form; this.variant = form; }
      return this.variant;
    }
    filter() {
      const slides = [...this.gallery.querySelectorAll('.product__media-item[data-media-id]')];
      const variant = this.selectedVariant(), color = this.data.variantColors[variant];
      const result = resolve(this.data.config, color, slides.map(slide => identity(slide.dataset.mediaId)));
      result.changed = color !== this.color; this.color = color;
      const allowed = new Set(result.ids);
      const toggle = (element, id) => {
        const hidden = !!id && !allowed.has(id);
        element.toggleAttribute('data-erp-color-hidden', hidden);
        element.classList.toggle('hidden', hidden);
        if (hidden) { element.setAttribute('aria-hidden', 'true'); element.querySelectorAll('video').forEach(video => video.pause()); }
        else element.removeAttribute('aria-hidden');
      };
      this.gallery.querySelectorAll('.product__media-item[data-media-id], [data-target]').forEach(element => toggle(element, identity(element.dataset.mediaId || element.dataset.target)));
      this.modal = this.document.getElementById(`ProductModal-${this.section}`);
      this.modal?.querySelectorAll('[data-media-id]').forEach(element => toggle(element, identity(element.dataset.mediaId)));
      this.root?.querySelector(`style[data-erp-color-initial="${this.section}"]`)?.remove();
      this.gallery.updateStackedLayoutFirstVisible?.();
      return result;
    }
    refreshSliders() {
      this.gallery.querySelectorAll('slider-component').forEach(slider => {
        const active = this.gallery.querySelector('.product__media-item.is-active');
        const target = slider.querySelector('[data-media-id="' + (active?.dataset.mediaId || '') + '"], [data-target="' + (active?.dataset.mediaId || '') + '"]');
        // Reset scroll before native pagination: an old far-right position can otherwise index a removed slide.
        if (slider.slider) slider.slider.scrollLeft = target?.offsetLeft || 0;
        slider.resetPages?.();
        const visible = [...slider.querySelectorAll('[id^="Slide-"]')].filter(item => !item.hasAttribute('data-erp-color-hidden') && item.clientWidth > 0);
        // LVS's native initPages returns early for one slide; reset its previous multi-slide state.
        if (visible.length < 2) {
          slider.sliderItemsToShow = visible;
          slider.currentPage = 1; slider.totalPages = 1; slider.slidesPerPage = 1;
          slider.sliderItemOffset = Math.max(visible[0]?.clientWidth || 1, 1);
          if (slider.currentPageElement) slider.currentPageElement.textContent = '1';
          if (slider.pageTotalElement) slider.pageTotalElement.textContent = String(visible.length);
          slider.prevButton?.setAttribute('disabled', 'disabled'); slider.nextButton?.setAttribute('disabled', 'disabled');
        }
      });
      this.refreshDots();
    }
    refreshDots() {
      // Native dot links are indexed against every color. Reindex only visible dots after filtering.
      const dots = [...this.gallery.querySelectorAll('.slider-counter__link')].filter(dot => !dot.hasAttribute('data-erp-color-hidden'));
      const current = identity(this.gallery.querySelector('.product__media-item.is-active')?.dataset.mediaId);
      dots.forEach(dot => {const active = identity(dot.dataset.target) === current; dot.classList.toggle('slider-counter__link--active', active); if (active) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current');});
    }
    update(variantId) {
      if (variantId) this.variant = String(variantId);
      const current = this.gallery.querySelector('.product__media-item.is-active')?.dataset.mediaId;
      this.gallery.setActiveMedia(current || '', true, false);
    }
  }
  window.ErpColorGallery = {resolve, identity, Controller};
  if (typeof document === 'undefined') return;
  const controllers = new Map();
  function mount() {
    for (const [gallery, controller] of controllers) if (!gallery.isConnected) {controller.resizeObserver?.disconnect(); controllers.delete(gallery);}
    document.querySelectorAll('media-gallery').forEach(gallery => {
      if (controllers.has(gallery) || typeof gallery.setActiveMedia !== 'function') return;
      const root = gallery.closest('product-info');
      const script = root?.querySelector(`script[data-erp-color-gallery="${gallery.dataset.section}"]`);
      if (!script) return;
      try {
        const data = JSON.parse(script.textContent);
        if (!data.variantColors || data.config?.enabled !== true) return;
        const controller = new Controller(gallery, data, document); controllers.set(gallery, controller); controller.update();
        if (typeof ResizeObserver !== 'undefined') {
          let width = gallery.clientWidth;
          controller.resizeObserver = new ResizeObserver(() => {
            if (gallery.clientWidth === width) return;
            width = gallery.clientWidth; controller.refreshSliders();
          });
          controller.resizeObserver.observe(gallery);
        }
      } catch (_) { /* Leave the original gallery available if a configuration is invalid. */ }
    });
  }
  const update = (section, variantId) => {for (const [gallery, controller] of controllers) if (gallery.isConnected && String(section) === controller.section) controller.update(variantId);};
  document.addEventListener('variant:change', event => update(event.detail?.sectionId, event.detail?.variant?.id));
  // LVS's DOM event is throttled; the native pub/sub event also covers quick consecutive selections.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.variantChange, event => update(event.data?.sectionId, event.data?.variant?.id));
  document.addEventListener('shopify:section:load', mount);
  window.addEventListener('popstate', () => {const id = new URLSearchParams(window.location.search).get('variant');for (const controller of controllers.values()) if (id && Object.hasOwn(controller.data.variantColors, id)) controller.update(id);});
  customElements.whenDefined('media-gallery').then(mount);
  new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && (node.matches?.('product-info,media-gallery') || node.querySelector?.('media-gallery'))))) mount();
  }).observe(document.documentElement, {childList:true,subtree:true});
  const style = document.createElement('style'); style.textContent = '[data-erp-color-hidden]{display:none!important}'; document.head.append(style);
})();
